import React, {useEffect, useRef} from 'react'

import {MetrikaContext} from './Context'
import {DataLayer} from './data-layer'
import IFrame from './IFrame'
import {GoogleAnalytics, Metrika, YandexMetrika} from './types'
import {attachScript, emitGoogleQueue, emitYandexQueue, emptyMetrika} from './utils'

type Props = {
  brand?: 'yandex' | 'yango'
  clientID?: string
  googleLink?: string
  gtmLink?: string
  yandexID?: number
}

const MetrikaProvider: React.FC<React.PropsWithChildren<Props>> = ({
  brand = 'yandex',
  children,
  clientID,
  yandexID,
  gtmLink,
  googleLink,
}) => {
  const metrika = useRef<Metrika>(emptyMetrika)

  useEffect(() => {
    Promise.all([
      yandexID
        ? attachScript(
            brand === 'yango' ? 'https://mc.yango.com/metrika/tag.js' : 'https://mc.yandex.ru/metrika/tag.js',
            {crossOrigin: 'anonymous', async: true},
          )
        : undefined,
    ]).then(() => {
      if (yandexID && window.Ya) {
        const yaCounter = new window.Ya.Metrika2({
          id: yandexID,
          clickmap: true,
          trackLinks: true,
          accurateTrackBounce: true,
          webvisor: true,
          params: {clientID},
        })
        const withBaseParams: <T>(params: T) => T & {clientID?: string} = params => ({clientID, ...params})
        const yandexMetrika: YandexMetrika = {
          reachGoal: (goal, params) => yaCounter.reachGoal(goal, withBaseParams(params)),
          setVisitParams: params => yaCounter.params(withBaseParams(params)),
          trackVirtualPage: yaCounter.hit,
        }

        emitYandexQueue(yandexMetrika)
        Object.assign(metrika.current, yandexMetrika)
      }

      if (googleLink) {
        const iframe = window.document.querySelector<HTMLIFrameElement>('iframe[title=analytics]')
        const googleAnalytics: GoogleAnalytics = {
          trackGA: goal => iframe?.contentWindow?.postMessage(goal, '*'),
        }

        emitGoogleQueue(googleAnalytics)
        Object.assign(metrika.current, googleAnalytics)
      }
    })
  }, [clientID, yandexID, googleLink, brand])

  return (
    <MetrikaContext.Provider value={metrika}>
      {children}
      {gtmLink && <DataLayer link={gtmLink} />}
      {googleLink && <IFrame link={googleLink} title="analytics" />}
    </MetrikaContext.Provider>
  )
}

export default MetrikaProvider
